import React, { useEffect, useState } from "react";

import {
  calculateFootprintingProgress,
  calculateRemovalsProgress,
  computeCarbonProgramImpact,
  formatCurrencyRange,
  formatFractionDisplay,
  formatNumericCurrency,
  getFarmIdFromLocalStorage,
  isFeatureActive,
  isSingleFieldOutcomes,
  useGetAllCarbonProgramOutcomes,
  useGetFarmFeatureFlags,
  useGetFarmReportingYear,
  useGetFieldsByFarmId,
  useGetFootprintingCropFootprints,
  useGetFootprintingDairyFootprints,
  useGetFootprintingFarmFootprint,
  useInventoryImpact,
  useNavigate,
  useTranslation,
  useUserContext,
} from "utils";

import { FEATURE_FLAG_SLUGS } from "consts";

import { ACCOUNTING_METHODOLOGY, CarbonProgram, FarmResponse, SingleFieldOutcomes } from "models";

import {
  Card,
  ContextMenu,
  DeleteFarmModal,
  Drawer,
  Flex,
  Icon,
  IconButton,
  SkeletonWrapper,
  TagNormal,
  Tooltip,
  TooltipWrapper,
  Typography,
} from "components";

import { FarmDetails } from "pages/app/farm/components/FarmDetails";
import { SOIL_PLUS_3_START_YEAR, allotmentCappedPayout } from "pages/app/field-timeline/utils/PayoutUtils";

interface HomePageFarmCardProps {
  farm: FarmResponse;
  onFarmUpdated: () => void;
  onFarmDeleted?: (farmId: string) => void;
  isCurrentFarm?: boolean;
  onFarmSelect?: (farmId: string) => void;
}

interface FarmCardRowProps {
  title: string;
  dataTestId: string;
  handleClick: () => void;
  progress: number;
  value: string;
  hasPayoutRange: boolean;
}

interface ContentRowProps {
  farm: FarmResponse;
  isInventoryProgram?: boolean;
  carbonProgram?: CarbonProgram | null;
}

const FarmCardRow = ({ title, dataTestId, handleClick, progress, value, hasPayoutRange }: FarmCardRowProps) => (
  <Flex className="assessment-rows" data-testid={dataTestId} onClick={handleClick} gap="1rem">
    <Flex flex={12} className="assessment-cell">
      <Typography variant="paragraph-l" className="margin-0">
        {title}
      </Typography>
    </Flex>
    <Flex flex={hasPayoutRange ? 4 : 5} justify="flex-start">
      <Typography variant="paragraph-l" className="margin-0">
        {progress}%
      </Typography>
    </Flex>
    <Flex flex={hasPayoutRange ? 8 : 5} justify="flex-start" className="overflow-hidden">
      <Tooltip title={value} placement="topLeft" className="farm-card-payout-tooltip">
        <Typography variant="paragraph-l" className="margin-0 farm-card-payout">
          {value}
        </Typography>
      </Tooltip>
    </Flex>
    <Flex flex={2} justify="flex-end" align="center">
      <Icon iconName="tabler/chevron-right" />
    </Flex>
  </Flex>
);

const RemovalRow = ({ farm, carbonProgram }: ContentRowProps) => {
  useGetFieldsByFarmId({ farmId: farm.id });
  const navigate = useNavigate();
  const { t } = useTranslation("dashboard");
  const currentYear = new Date().getFullYear();
  const { user, switchFarm, reloadUserData } = useUserContext();

  const [shouldNavigateToFarmPage, setShouldNavigateToFarmPage] = useState<boolean>(false);

  const { data: programOutcomes } = useGetAllCarbonProgramOutcomes(farm.id, farm.carbonProgram);
  const singleFieldOutcomes: SingleFieldOutcomes[] = Object.values(programOutcomes ?? {}).filter(isSingleFieldOutcomes);
  const {
    hideInventoryPayouts,
    totalPayoutMin: inventoryPayoutMin,
    totalPayoutMax: inventoryPayoutMax,
    totalPracticeBasedPayout,
  } = useInventoryImpact(currentYear, singleFieldOutcomes, farm?.id ?? "", farm?.carbonProgram ?? undefined);
  const {
    data: { activeFarmReportingYear },
  } = useGetFarmReportingYear(farm.id);

  const progress = calculateRemovalsProgress(farm.carbonProgram, programOutcomes, currentYear);

  const isInventoryProgram = carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

  const soilPlus3Data = singleFieldOutcomes.find((field) => field.soilPlus3Data)?.soilPlus3Data;

  // the card keeps its rewards-flag rule, like the fields page; the allotment maths is shared with the new home page
  const { data: farmFeatureFlags } = useGetFarmFeatureFlags({ farmId: farm.id });
  const shouldUseAllotmentPayout =
    isFeatureActive(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_REWARDS, farmFeatureFlags) && !!activeFarmReportingYear && activeFarmReportingYear >= SOIL_PLUS_3_START_YEAR;

  let payout: number | string = 0;

  if (isInventoryProgram) {
    const showOutcomeBasedIncentives = !!carbonProgram?.platformFeatures.fieldManagement?.outcomeBasedIncentives;
    payout = hideInventoryPayouts
      ? "\u2014"
      : showOutcomeBasedIncentives
      ? formatCurrencyRange(inventoryPayoutMin, inventoryPayoutMax, { decimals: 0, currency: farm.carbonProgram?.currency })
      : formatCurrencyRange(totalPracticeBasedPayout, totalPracticeBasedPayout, { decimals: 0, currency: farm.carbonProgram?.currency });
  } else {
    const { totalPayoutMin, totalPayoutAvg } = programOutcomes
      ? computeCarbonProgramImpact(programOutcomes, currentYear)
      : { totalPayoutMin: 0, totalPayoutAvg: 0 };

    if (shouldUseAllotmentPayout) {
      const pointPrice = farm.carbonProgram?.fieldsManagement.payouts?.removals?.pointPrice ?? 0;
      const capped = allotmentCappedPayout(soilPlus3Data, pointPrice, totalPayoutMin);
      payout = capped === null ? "\u2014" : formatNumericCurrency(capped, { decimals: 0, currency: farm.carbonProgram?.currency });
    } else {
      payout = formatNumericCurrency(totalPayoutAvg, { decimals: 0, currency: farm.carbonProgram?.currency });
    }
  }

  useEffect(() => {
    if (!shouldNavigateToFarmPage) return;
    if (farm.id !== user?.farm.id) return;

    setShouldNavigateToFarmPage(false);
    navigate("/all-fields");
  }, [navigate, shouldNavigateToFarmPage, user?.farm.id, farm.id]);

  const handleFarmClick = () => {
    const localFarmId = getFarmIdFromLocalStorage();

    if (localFarmId !== farm.id) {
      switchFarm(farm.id);
      reloadUserData();
    }
    setShouldNavigateToFarmPage(true);
  };

  return (
    <FarmCardRow
      dataTestId="farm-list-card-removals-row"
      title={t("farmCard.removals")}
      handleClick={handleFarmClick}
      progress={progress || 0}
      value={payout}
      hasPayoutRange={isInventoryProgram}
    />
  );
};

const FootprintingRow = ({ farm, isInventoryProgram }: ContentRowProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation("dashboard");
  const currentYear = new Date().getFullYear();
  const { user, switchFarm, reloadUserData } = useUserContext();
  const [shouldNavigateToFootprintingPage, setShouldNavigateToFootprintingPage] = useState<boolean>(false);

  const farmProgressQuery = useGetFootprintingFarmFootprint(farm.id, currentYear);
  const cropProgressQueries = useGetFootprintingCropFootprints(farm.id, [currentYear], farm.carbonProgram?.farmLevelFootprinting?.programCrops);
  const dairyProgressQueries = useGetFootprintingDairyFootprints(farm.id, [currentYear]);

  const { completionRate, totalPayout } = calculateFootprintingProgress(farmProgressQuery.data, [
    ...cropProgressQueries.filter((cpq) => !!cpq.data).map((cpq) => cpq.data),
    ...dairyProgressQueries.filter((dpq) => !!dpq.data).map((dpq) => dpq.data),
  ]);

  useEffect(() => {
    if (!shouldNavigateToFootprintingPage) return;
    if (farm.id !== user?.farm.id) return;

    setShouldNavigateToFootprintingPage(false);
    navigate("/footprinting");
  }, [navigate, shouldNavigateToFootprintingPage, user?.farm.id, farm.id]);

  const handleFarmClick = () => {
    const localFarmId = getFarmIdFromLocalStorage();

    if (localFarmId !== farm.id) {
      switchFarm(farm.id);
      reloadUserData();
    }
    setShouldNavigateToFootprintingPage(true);
  };

  const hasFootprintingPayouts = farm.carbonProgram?.platformFeatures?.footprinting?.payouts ?? false;

  return (
    <FarmCardRow
      dataTestId="farm-list-card-footprinting-row"
      title={t("farmCard.footprinting")}
      handleClick={handleFarmClick}
      progress={formatFractionDisplay(completionRate) || 0}
      value={hasFootprintingPayouts ? formatNumericCurrency(totalPayout, { decimals: 0, currency: farm.carbonProgram?.currency }) : "\u2014"}
      hasPayoutRange={!!isInventoryProgram}
    />
  );
};

const HomePageFarmCard = ({ farm, isCurrentFarm = false, onFarmSelect, onFarmDeleted }: HomePageFarmCardProps) => {
  const { t } = useTranslation(["dashboard", "farm", "onboarding"]);

  const { isLoading } = useGetAllCarbonProgramOutcomes(farm.id, farm.carbonProgram);

  const [showDeleteModalConfirm, setShowDeleteModalConfirm] = useState(false);
  const [showFarmDetailsDrawer, setShowFarmDetailsDrawer] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);

  const isInventoryProgram = farm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

  const handleCardClick = () => {
    if (onFarmSelect) {
      onFarmSelect(farm.id);
    }
  };

  const handleDrawerClose = () => {
    setShowFarmDetailsDrawer(false);
  };

  const handleFarmDetailsFinish = () => {
    setShowFarmDetailsDrawer(false);
  };

  const shouldShowFootprintingRow = farm.carbonProgram?.platformFeatures?.footprinting?.enabled === true;
  const shouldShowRemovalsRow = farm.carbonProgram?.platformFeatures?.fieldManagement?.enabled ?? true; // for any CP that does not want removals.

  const actions = [
    {
      iconName: "tabler/edit",
      label: t("farm:farmContextMenu.editFarm"),
      onClick: () => setShowFarmDetailsDrawer(true),
      "data-testid": "farm-edit-action",
    },
    {
      iconName: "tabler/trash",
      label: t("farm:farmContextMenu.deleteFarm"),
      onClick: () => setShowDeleteModalConfirm(true),
      "data-testid": "farm-delete-action",
    },
  ];

  return (
    <>
      <SkeletonWrapper type="block" isLoading={isLoading} data-testid="skeleton">
        <Card dataTestId="home-farm-card" className={`farm-card ${isCurrentFarm ? "current-farm" : ""}`} onClick={handleCardClick}>
          <Flex vertical className="padding-top-150 padding-bottom-100">
            <Flex className="padding-bottom-100 padding-left-150 padding-right-150" justify="space-between" align="center" gap="0.5rem">
              <Tooltip className="farm-card-name-wrapper" title={farm.name}>
                <Typography variant="h3" className="overflow-hidden ellipsis">
                  {farm.name}
                </Typography>
              </Tooltip>
              <Flex align="center" gap="0.5rem" className="farm-card-actions">
                {farm.carbonProgram?.programName && (
                  <Tooltip variant="neutral" title={t("farmCard.infoMessageOnFarmCardTooltip")}>
                    <TagNormal
                      text={farm.carbonProgram?.programName}
                      color="grey"
                      className="farm-card-tag text-black-600"
                      icon="tabler/info-circle"
                      dataTestId="farm-info-tag"
                      variant="paragraph-s"
                    />
                  </Tooltip>
                )}
                <ContextMenu
                  items={actions.map((action) => ({
                    iconName: action.iconName,
                    label: action.label,
                    onClick: action.onClick,
                  }))}
                  className="farm-actions-context-menu"
                  iconSize="m-light"
                  placement="bottom-right"
                  visible={showActionMenu}
                  onClose={() => setShowActionMenu(false)}
                  dataTestId="farm-actions-context-menu"
                  iconPlacement="left"
                >
                  <TooltipWrapper
                    tooltip={{
                      title: t("farm:farmContextMenu.editFarm"),
                    }}
                  >
                    <IconButton
                      dataTestId="farm-actions-context-menu-icon"
                      className="farm-actions-context-menu-icon"
                      iconName="tabler/dots-vertical"
                      size="small"
                      type="tertiary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowActionMenu(true);
                      }}
                    />
                  </TooltipWrapper>
                </ContextMenu>
              </Flex>
            </Flex>
            <Flex className="padding-left-150 padding-top-50 padding-bottom-100 padding-right-150" data-testid="grid-header" gap="1rem">
              <Flex flex={12} data-testid="assessment-header">
                <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                  {t("dashboard:farmCard.assessment")}
                </Typography>
              </Flex>
              <Flex flex={isInventoryProgram ? 4 : 5} data-testid="progress-header">
                <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                  {t("dashboard:farmCard.progress")}
                </Typography>
              </Flex>
              <Flex flex={isInventoryProgram ? 8 : 5} data-testid="payout-header">
                <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                  {t("dashboard:farmCard.payout")}
                </Typography>
              </Flex>
              <Flex flex={2}>&#8203;</Flex>
            </Flex>
            {shouldShowRemovalsRow && <RemovalRow farm={farm} carbonProgram={farm?.carbonProgram} />}
            {shouldShowFootprintingRow && <FootprintingRow farm={farm} isInventoryProgram={isInventoryProgram} />}
          </Flex>
        </Card>
      </SkeletonWrapper>
      <DeleteFarmModal
        farm={showDeleteModalConfirm ? { id: farm.id, name: farm.name } : null}
        onClose={() => setShowDeleteModalConfirm(false)}
        onDeleted={(farmId) => onFarmDeleted?.(farmId)}
      />
      {showFarmDetailsDrawer && (
        <Drawer
          data-testid="farm-details-drawer"
          visible={showFarmDetailsDrawer}
          title={t("onboarding:onboardingFormHeader.editFarm")}
          onClose={handleDrawerClose}
          footer={undefined}
        >
          <FarmDetails selectedFarmId={farm.id} onFinish={handleFarmDetailsFinish} isOnboarding={false} handleBack={handleFarmDetailsFinish} />
        </Drawer>
      )}
    </>
  );
};

export default HomePageFarmCard;
